import { defineStore } from 'pinia'
import { ref, computed, readonly, watch } from 'vue'
import { apolloClient } from '@/lib/apollo'
import { MENU_ITEMS_QUERY, MENU_PERMISSIONS_QUERY, NAVIGATION_TREE_QUERY, ALL_NAVIGATION_TREE_QUERY, UPDATE_MENU_PERMISSION_MUTATION } from '@/graphql/menu'
import { useAuthStore } from './auth'

export interface MenuPermission {
  id: number
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  role: {
    name: string
  }
}

export interface NavigationPermission {
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  inherited: boolean
}

export interface NavigationItem {
  id: string
  name: string
  path: string
  icon?: string
  adminOnly: boolean
  sortOrder: number
  category?: string | null
  parentId?: string | null
  visibleInSidebar: boolean
  routeName?: string | null
  kind: 'SCREEN' | 'GROUP' | 'PERSONAL' | 'HIDDEN'
  permission: NavigationPermission
  children: NavigationItem[]
}

export interface NavigationCategory {
  id: string
  name: string
  items: NavigationItem[]
}

export interface MenuItem {
  id: string
  name: string
  path: string
  icon?: string
  adminOnly: boolean
  sortOrder: number
  category?: string | null
  parentId?: string | null
  visibleInSidebar: boolean
  routeName?: string | null
  kind: 'SCREEN' | 'GROUP' | 'PERSONAL' | 'HIDDEN'
  permissions: MenuPermission[]
  children?: Array<{ id: string }>
}

export const usePermissionsStore = defineStore('permissions', () => {
  const menuItems = ref<MenuItem[]>([])
  const navigationTree = ref<NavigationCategory[]>([])
  const fullNavigationTree = ref<NavigationCategory[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  const authStore = useAuthStore()

  const menuMap = computed(() => {
    const map = new Map<string, MenuItem>()
    for (const item of menuItems.value) {
      map.set(item.id, item)
    }
    return map
  })

  const flatNavigationItems = computed(() => {
    const items: NavigationItem[] = []
    const visit = (node: NavigationItem) => {
      items.push(node)
      for (const child of node.children || []) visit(child)
    }
    for (const category of navigationTree.value) {
      for (const item of category.items) visit(item)
    }
    return items
  })

  const navigationMap = computed(() => {
    const map = new Map<string, NavigationItem>()
    for (const item of flatNavigationItems.value) {
      map.set(item.id, item)
    }
    return map
  })

  const flatFullNavigationItems = computed(() => {
    const items: NavigationItem[] = []
    const visit = (node: NavigationItem) => {
      items.push(node)
      for (const child of node.children || []) visit(child)
    }
    for (const category of fullNavigationTree.value) {
      for (const item of category.items) visit(item)
    }
    return items
  })

  const fullNavigationMap = computed(() => {
    const map = new Map<string, NavigationItem>()
    for (const item of flatFullNavigationItems.value) {
      map.set(item.id, item)
    }
    return map
  })

  const hasVisibleAccess = (item: NavigationItem): boolean => {
    if (!item.visibleInSidebar) return false
    if (authStore.isAdmin) return true

    const selfVisible = item.permission.canView || item.permission.canEdit || item.permission.canDelete
    const visibleChildren = (item.children || []).filter(hasVisibleAccess)

    if (item.kind === 'GROUP') {
      return visibleChildren.length > 0 || selfVisible
    }

    return selfVisible
  }

  const filterNavigationItems = (items: NavigationItem[]): NavigationItem[] =>
    items
      .map((item) => {
        const children = filterNavigationItems(item.children || [])
        return {
          ...item,
          children,
        }
      })
      .filter(hasVisibleAccess)

  const visibleSidebarTree = computed(() =>
    navigationTree.value
      .map((category) => ({
        ...category,
        items: filterNavigationItems(category.items || []),
      }))
      .filter((category) => category.items.length > 0)
  )

  const fullSidebarTree = computed(() =>
    fullNavigationTree.value
      .map((category) => ({
        ...category,
        items: filterNavigationItems(category.items || []),
      }))
      .filter((category) => category.items.length > 0)
  )

  const authorizedMenus = computed(() => {
    if (!authStore.user) return []
    if (authStore.isAdmin) return menuItems.value.slice().sort((a, b) => a.sortOrder - b.sortOrder)

    return menuItems.value
      .filter((menu) => {
        const navItem = navigationMap.value.get(menu.id)
        if (navItem) {
          return navItem.permission.canView || navItem.permission.canEdit || navItem.permission.canDelete
        }

        return menu.permissions.some((perm) => perm.canView || perm.canEdit || perm.canDelete)
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
  })

  function hasPermission(menuId: string, permission: 'view' | 'edit' | 'delete'): boolean {
    if (!authStore.user) return false
    if (authStore.isAdmin) return true

    const navItem = navigationMap.value.get(menuId)
    if (navItem) {
      switch (permission) {
        case 'view': return navItem.permission.canView
        case 'edit': return navItem.permission.canEdit
        case 'delete': return navItem.permission.canDelete
        default: return false
      }
    }

    const menu = menuMap.value.get(menuId)
    if (!menu) return false

    const userRoles = authStore.user.roles.map((r) => r.name)

    return menu.permissions.some((perm) => {
      const roleMatches = userRoles.includes(perm.role.name)
      switch (permission) {
        case 'view': return roleMatches && perm.canView
        case 'edit': return roleMatches && perm.canEdit
        case 'delete': return roleMatches && perm.canDelete
        default: return false
      }
    })
  }

  async function fetchMenuItems(force = false) {
    if (!authStore.user) {
      return
    }

    if (!force && initialized.value) {
      console.log('⏭️ Skipping fetch - already initialized')
      return
    }

    if (!force && loading.value) {
      console.log('⏳ Waiting for in-flight permissions fetch')
      while (loading.value) {
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
      return
    }

    try {
      loading.value = true
      error.value = null

      const [{ data }, permsResult, navigationResult, fullNavigationResult] = await Promise.all([
        apolloClient.query({
          query: MENU_ITEMS_QUERY,
          fetchPolicy: 'network-only'
        }),
        apolloClient.query({
          query: MENU_PERMISSIONS_QUERY,
          fetchPolicy: 'network-only'
        }),
        apolloClient.query({
          query: NAVIGATION_TREE_QUERY,
          fetchPolicy: 'network-only'
        }),
        authStore.isAdmin
          ? apolloClient.query({
              query: ALL_NAVIGATION_TREE_QUERY,
              fetchPolicy: 'network-only'
            })
          : Promise.resolve({ data: { allNavigationTree: [] } })
      ])

      const permissionsByMenu: Record<string, any[]> = {}
      if (permsResult.data?.menuPermissions) {
        for (const p of permsResult.data.menuPermissions) {
          const menuId = p.menuId ?? p.MenuItem?.id
          if (!menuId) continue
          if (!permissionsByMenu[menuId]) permissionsByMenu[menuId] = []
          permissionsByMenu[menuId].push({
            id: p.id,
            canView: p.canView,
            canEdit: p.canEdit,
            canDelete: p.canDelete,
            role: { name: p.Role?.name }
          })
        }
      }

      if (data?.menuItems) {
        menuItems.value = data.menuItems.map((mi: any) => ({
          ...mi,
          permissions: permissionsByMenu[mi.id] ?? []
        }))
      }

      navigationTree.value = navigationResult.data?.navigationTree ?? []
      fullNavigationTree.value = authStore.isAdmin
        ? (fullNavigationResult.data?.allNavigationTree ?? navigationResult.data?.navigationTree ?? [])
        : (navigationResult.data?.navigationTree ?? [])
      initialized.value = true
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement des menus'
      console.error('❌ Failed to fetch menu items:', err)
    } finally {
      loading.value = false
    }
  }

  async function updateMenuPermission(input: {
    menuId: string
    roleId: number
    canView: boolean
    canEdit: boolean
    canDelete: boolean
  }) {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_MENU_PERMISSION_MUTATION,
        variables: { input }
      })

      if (data?.updateMenuPermission) {
        await fetchMenuItems(true)
      }
    } catch (err: any) {
      error.value = err.message || 'Erreur lors de la mise à jour des permissions'
      throw err
    }
  }

  function reset() {
    menuItems.value = []
    navigationTree.value = []
    fullNavigationTree.value = []
    loading.value = false
    error.value = null
    initialized.value = false
    console.log('🔄 Permissions store reset')
  }

  watch(
    () => authStore.user?.id,
    (nextUserId, previousUserId) => {
      if (nextUserId !== previousUserId) {
        reset()
      }
    }
  )

  return {
    menuItems: readonly(menuItems),
    navigationTree: readonly(navigationTree),
    fullNavigationTree: readonly(fullNavigationTree),
    loading: readonly(loading),
    error: readonly(error),
    initialized: readonly(initialized),
    authorizedMenus,
    visibleSidebarTree,
    fullSidebarTree,
    hasPermission,
    hasAccess: hasPermission,
    fetchMenuItems,
    updateMenuPermission,
    reset
  }
})
